import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useQueryClient } from '@zg-rentals/react-query5';

import { Flex, Spacer } from '@zillow/constellation';
import { Button, Heading, Paragraph, Text, TextButton, Toast, useToast } from '@zillow/constellation-10';

import { useSelector } from '../../../../selectors/useSelector';
import { PERMISSION_CARD_TEXT, PERMISSION_REQUEST_TYPE } from '../../../../constants/ApplicationPermissionsConstants';

import PropertiesActions from '../../../../actions/PropertiesActions';

import UserManagementActions from '../../../../actions/UserManagementActions';
import { RoutingSelectors, UserManagementSelectors, UserSelectors } from '../../../../selectors';
import stringHelper from '../../../../utils/stringHelper';
import applicationUtils from '../../../../utils/applicationUtils';
import { StyledBlueCard } from '../../StyledComponents';

import type { ApplicationsSummary } from '../../interfaces';

type Props = {
  permissionType: keyof typeof PERMISSION_REQUEST_TYPE;
  onSetApplicationManagerClick: () => void;
  applicationsSummary: ApplicationsSummary;
};
const ApplicationPermissionCard = ({ applicationsSummary, permissionType, onSetApplicationManagerClick }: Props) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { enqueueToast } = useToast();

  const listingId = useSelector((state) => RoutingSelectors.makeListingId(state));
  const applicationManager = useSelector((state) => UserManagementSelectors.makeApplicationManager(state));
  const user = useSelector((state) => UserSelectors.makeUser(state));

  const [permissionRequestSent, setPermissionRequestSent] = useState<boolean | undefined>();

  useEffect(() => {
    if (applicationsSummary.userManagementFlags) {
      setPermissionRequestSent(applicationsSummary.userManagementFlags.isPermissionRequestSent);
    }
  }, [applicationsSummary]);

  if (!applicationsSummary) {
    return null;
  }

  const {
    isApplicationsAccepted,
    rentalApplicationData: { totalNumberOfApplications },
  } = applicationsSummary;

  const shouldRenderControls =
    permissionType === PERMISSION_REQUEST_TYPE.SET_APPLICATION_MANAGER ||
    permissionType === PERMISSION_REQUEST_TYPE.REQUEST_ACCESS;

  const handleRequestAccessClick = () => {
    dispatch(UserManagementActions.requestApplicationAccess(user.userManagementCompanyId))
      // @ts-expect-error
      .then(() => {
        const appManagerName = applicationUtils.getName(applicationManager || {});
        const successBody = `We’ve sent your application permission request to
                      your application manager ${stringHelper.wrapText(appManagerName, 10)}`;
        enqueueToast(
          <Toast tone="success" className="fs-mask">
            {successBody}
          </Toast>,
        );
        queryClient.invalidateQueries({ queryKey: ['getApplicationsSummary', listingId] });
        dispatch(
          PropertiesActions.updateCurrentProperty({
            ...applicationsSummary,
            userManagementFlags: { ...applicationsSummary.userManagementFlags, isPermissionRequestSent: true },
          }),
        );
        setPermissionRequestSent(true);
      })
      .catch(() => {
        enqueueToast(<Toast tone="critical">Could not send your request, please try again later.</Toast>, {
          duration: 10000,
        });
      });
  };

  const handleButtonClick =
    permissionType === PERMISSION_REQUEST_TYPE.SET_APPLICATION_MANAGER
      ? onSetApplicationManagerClick
      : handleRequestAccessClick;

  const renderControls = () => {
    if (permissionRequestSent) {
      return (
        <Flex display="flex" marginTop="sm">
          <Text textStyle="body-sm">Waiting for permission</Text>
          <Spacer paddingLeft="sm">
            <TextButton onClick={handleButtonClick}>Re-send request</TextButton>
          </Spacer>
        </Flex>
      );
    }
    return (
      <Button css={{ marginTop: '600' }} tone="brand" emphasis="filled" fluid onClick={handleButtonClick}>
        {/* @ts-expect-error */}
        {PERMISSION_CARD_TEXT[permissionType]?.buttonText}
      </Button>
    );
  };

  const renderHeadingText = () => {
    if (totalNumberOfApplications > 0) {
      return `${stringHelper.pluralize(totalNumberOfApplications, 'application', 'applications')} awaiting review`;
    }

    if (isApplicationsAccepted) {
      return 'This property is accepting applications';
    }

    return 'This property does not currently accept applications';
  };

  return (
    <StyledBlueCard data-testid="ApplicationCompanyNoPermissionCard" cardType="secondary">
      <Heading level={6} css={{ marginBottom: '600' }}>
        {renderHeadingText()}
      </Heading>
      <Paragraph textStyle="body-sm-bold" css={{ marginBottom: '400' }}>
        {PERMISSION_CARD_TEXT[permissionType]?.subHeading}
      </Paragraph>
      <Paragraph textStyle="body-sm">{PERMISSION_CARD_TEXT[permissionType]?.body}</Paragraph>
      {shouldRenderControls && renderControls()}
    </StyledBlueCard>
  );
};

export default ApplicationPermissionCard;
